import React, { useMemo, useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import JournalEntryContext from "../utils/JournalEntryHelper";
import CurrencyContext from "../utils/CurrencyHelper";
import { MwSpinnerButton } from "../components/ui";
import { useTenant } from "../hooks";

const getEntryDetails = (entry) =>
  entry?.details || entry?.detail_relation || entry?.lines || [];

export const Dashboard = () => {
  const [t] = useTranslation("global");
  const lang = i18next.language === "ar" ? "ar" : "en";
  const { entries: journalEntries = [], selectedPeriod, selectedPeriodDetails, loading } = React.useContext(JournalEntryContext);
  const { currencies = [] } = React.useContext(CurrencyContext);
  const { tenantId, companySelected, branchSelected, getSettingsByCompanySelected } = useTenant();
  const baseurl = process.env.REACT_APP_INV_API_SERVER_BASE_URL;
  
  const [dashboardData, setDashboardData] = useState({
    count_chart_accounts: 0,
    count_chart_cost_center_accounts: 0,
    count_transactions: 0,
    monthly_transactions_counts: [],
    count_transactions_completed: 0,
    total_cr: 0,
    total_de: 0,
  });

  useEffect(() => {
    let ignore = false;

    const fetchDashboardData = async () => {
      if (!baseurl || !selectedPeriod || !tenantId || !companySelected?.value || !branchSelected?.value) {
        setDashboardData({
          count_chart_accounts: 0,
          count_chart_cost_center_accounts: 0,
          count_transactions: 0,
          monthly_transactions_counts: [],
          count_transactions_completed: 0,
          total_cr: 0,
          total_de: 0,
        });
        return;
      }

      try {
        const response = await axios.post(`${baseurl}/dashboard/home`, {
          fin_year_id: selectedPeriod,
          branch_id: branchSelected.value,
          tenant_id: tenantId,
          company_id: companySelected.value,
        });

        const responseData = response?.data?.data || {};
        if (ignore) return;

        setDashboardData({
          count_chart_accounts: Number(responseData?.count_chart_accounts) || 0,
          count_chart_cost_center_accounts: Number(responseData?.count_chart_cost_center_accounts) || 0,
          count_transactions: Number(responseData?.count_transactions) || 0,
          monthly_transactions_counts: responseData?.monthly_transactions_counts || [],
          count_transactions_completed: Number(responseData?.count_transactions_completed) || 0,
          total_cr: Number(responseData?.total_cr) || 0,
          total_de: Number(responseData?.total_de) || 0,
        });
      } catch (error) {
        console.error("Failed to fetch dashboard data:", error?.message || error);
        if (!ignore) {
          setDashboardData({
            count_chart_accounts: 0,
            count_chart_cost_center_accounts: 0,
            count_transactions: 0,
            monthly_transactions_counts: [],
            count_transactions_completed: 0,
            total_cr: 0,
            total_de: 0,
          });
        }
      }
    };

    fetchDashboardData();

    return () => {
      ignore = true;
    };
  }, [baseurl, selectedPeriod, tenantId, companySelected?.value, branchSelected?.value]);

  // Get period dates from localStorage
  const periodDates = useMemo(() => {
    const period = JSON.parse(localStorage.getItem("selectedPeriod") || "{}");
    return {
      startDate: period?.startDateDisplay,
      endDate: period?.endDateDisplay,
    };
  }, []);

  // Filter entries based on period dates for latest entries only
  const filteredEntries = useMemo(() => {
    if (!periodDates.startDate || !periodDates.endDate) return journalEntries;

    const startDate = new Date(periodDates.startDate);
    const endDate = new Date(periodDates.endDate);

    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
      return journalEntries;
    }

    endDate.setHours(23, 59, 59, 999);

    return journalEntries.filter((entry) => {
      if (!entry?.trans_date_m) return false;
      const entryDate = new Date(entry.trans_date_m);
      return entryDate >= startDate && entryDate <= endDate;
    });
  }, [journalEntries, periodDates]);

  // Extract data from API
  const totalAccounts = dashboardData.count_chart_accounts;
  const totalCostCenters = dashboardData.count_chart_cost_center_accounts;
  const totalEntries = dashboardData.count_transactions;
  const postedEntries = dashboardData.count_transactions_completed;
  const unpostedEntries = Math.max(totalEntries - postedEntries, 0);

  const moneyFormatter = useMemo(() => {
    const locale = lang === "ar" ? "en-US" : "en-US";
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }, [lang]);

  const formatMoney = (value) => moneyFormatter.format(Number(value || 0));

  const mainCurrencyCode = String(
    getSettingsByCompanySelected?.mainCurrency ||
      getSettingsByCompanySelected?.currency ||
      ""
  )
    .trim()
    .toUpperCase();

  const mainCurrency = useMemo(() => {
    if (!mainCurrencyCode) return "";
    const matchedCurrency = currencies.find((currency) => {
      const code = String(currency?.code).trim().toUpperCase();
      return code === mainCurrencyCode;
    });
    return matchedCurrency?.label || mainCurrencyCode;
  }, [currencies, mainCurrencyCode]);

  // Get month label helper
  const getMonthLabel = (monthNumber) => {
    const monthLabels = lang === "ar"
      ? ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"]
      : ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return monthLabels[Number(monthNumber) - 1] || "";
  };

  // Build monthly stats from API data
  const monthStats = useMemo(() => {
    const months = dashboardData.monthly_transactions_counts || [];
    if (!Array.isArray(months)) return [];
    
    // Create a map of existing months
    const monthMap = {};
    months.forEach((month) => {
      const monthNumber = Number(month.month) || 0;
      monthMap[monthNumber] = {
        key: `month_${monthNumber}`,
        month: monthNumber,
        label: getMonthLabel(monthNumber),
        count: Number(month.total || 0),
      };
    });
    
    // Ensure all 12 months are represented (missing months show 0)
    const allMonths = [];
    for (let i = 1; i <= 12; i++) {
      allMonths.push(
        monthMap[i] || {
          key: `month_${i}`,
          month: i,
          label: getMonthLabel(i),
          count: 0,
        }
      );
    }
    
    return allMonths;
  }, [dashboardData.monthly_transactions_counts, lang]);

  const maxMonthCount = Math.max(1, ...monthStats.map((month) => month.count || 0));
  const postedPercent = totalEntries
    ? Math.round((Math.min(postedEntries, totalEntries) / totalEntries) * 100)
    : 0;

  // Show loading spinner while data is loading
  if (loading) {
    return (
      <section className="p-4 flex justify-center items-center min-h-screen">
        <div className="flex flex-col items-center gap-4">
          <MwSpinnerButton />
          <p className="text-slate-500">{t("common.loading")}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="p-4">
      <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br from-[#f7f8ff] via-white to-[#e6f4ff] p-6 text-[#261759]">
        <div className="absolute -top-24 -right-20 h-56 w-56 rounded-full bg-[#dfeafe] opacity-70" />
        <div className="absolute -bottom-28 -left-20 h-56 w-56 rounded-full bg-[#e0f2fe] opacity-70" />

        <div className="relative z-0 flex flex-col gap-3">
          <h2 className="text-2xl font-semibold">{t("dashboard.title")}</h2>
          <p className="text-sm text-[#463977]">{t("dashboard.subtitle")}</p>

          <div className="mt-4 grid gap-4 md:grid-cols-4">
            <div className="rounded-xl border border-slate-200 bg-white/90 p-4 shadow-sm">
              <div className="text-xs text-slate-500">
                {t("dashboard.cards.accounts")}
              </div>
              <div className="mt-2 text-md font-semibold text-slate-700">
                {totalAccounts} {lang === "ar" ? "حساب" : "account"}
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white/90 p-4 shadow-sm">
              <div className="text-xs text-slate-500">
                {t("dashboard.cards.costCenters")}
              </div>
              <div className="mt-2 text-md font-semibold text-slate-700">
                {totalCostCenters}{" "}
                {lang === "ar" ? "مراكز تكلفة" : "cost centers"}
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white/90 p-4 shadow-sm">
              <div className="text-xs text-slate-500">
                {t("dashboard.cards.entries")}
              </div>
              <div className="mt-2 text-md font-semibold text-slate-700">
                {totalEntries} {lang === "ar" ? "قيد" : "entry"}
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white/90 p-4 shadow-sm">
              <div className="text-xs text-slate-500">
                {t("dashboard.cards.period")}
              </div>
              <div className="mt-2 text-sm font-semibold w-full flex flex-col gap-1 text-slate-700">
                <p className="flex items-center gap-1">
                  <span className="text-xs text-slate-500">
                    {lang === "ar" ? "بداية الفترة:" : "Period Start:"}
                  </span>
                  {selectedPeriodDetails?.startDateDisplay
                    ? selectedPeriodDetails.startDateDisplay
                    : t("common.noData")}
                </p>
                <p className="flex items-center gap-1">
                  <span className="text-xs text-slate-500">
                    {lang === "ar" ? "نهاية الفترة:" : "Period End:"}
                  </span>
                  {selectedPeriodDetails?.endDateDisplay
                    ? selectedPeriodDetails.endDateDisplay
                    : t("common.noData")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 text-[#261759] shadow-sm lg:col-span-2">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">
                {t("dashboard.charts.activity")}
              </h3>
              <p className="text-xs text-slate-500">
                {t("dashboard.charts.activityHint")}
              </p>
            </div>
            <div className="text-xs text-slate-500">
              {t("dashboard.cards.entries")}: {totalEntries}
            </div>
          </div>

          <div className="mt-6 flex items-end gap-3">
            {monthStats.map((month) => (
              <div
                key={month.key}
                className="flex flex-1 flex-col items-center gap-2"
              >
                <div className="w-full rounded-lg bg-slate-100 p-1">
                  <div
                    className="w-full rounded-md bg-[#2d6297] transition-all"
                    style={{
                      height: `${Math.max(
                        10,
                        (month.count / maxMonthCount) * 120,
                      )}px`,
                    }}
                  />
                </div>
                <span className="text-xs text-slate-500">{month.label}</span>
                <span className="text-xs font-semibold">{month.count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 text-[#261759] shadow-sm">
          <h3 className="text-lg font-semibold">
            {t("dashboard.charts.status")}
          </h3>
          <p className="text-xs text-slate-500">
            {t("dashboard.charts.statusHint")}
          </p>

          <div className="mt-6 flex flex-col items-center gap-4">
            <div
              className="relative flex h-40 w-40 items-center justify-center rounded-full"
              style={{
                background: `conic-gradient(#16a34a 0%, #16a34a ${postedPercent}%, #f59e0b ${postedPercent}%, #f59e0b 100%)`,
              }}
            >
              <div className="flex h-24 w-24 flex-col items-center justify-center rounded-full bg-white shadow-sm">
                <span className="text-sm text-slate-500">
                  {t("dashboard.cards.posted")}
                </span>
                <span className="text-xl font-semibold">{postedPercent}%</span>
              </div>
            </div>

            <div className="flex w-full items-center justify-between text-xs">
              <span className="text-emerald-600">
                {t("dashboard.cards.posted")}: {postedEntries}
              </span>
              <span className="text-amber-600">
                {t("dashboard.cards.unposted")}: {unpostedEntries}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 text-[#261759] shadow-sm">
          <h3 className="text-lg font-semibold">
            {t("dashboard.charts.balance")}
          </h3>
          <p className="text-xs text-slate-500">
            {t("dashboard.charts.balanceHint")}
          </p>
          <div className="mt-6 grid grid-cols-2 gap-4">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-xs text-slate-500">
                {t("dashboard.cards.totalDebit")}
              </div>
              <div className="mt-2 text-xl font-semibold">
                {formatMoney(dashboardData.total_de)}
              </div>
              <div className="mt-1 text-xs text-slate-500">
                {mainCurrency || t("common.noData")}
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-xs text-slate-500">
                {t("dashboard.cards.totalCredit")}
              </div>
              <div className="mt-2 text-xl font-semibold">
                {formatMoney(dashboardData.total_cr)}
              </div>
              <div className="mt-1 text-xs text-slate-500">
                {mainCurrency || t("common.noData")}
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 text-[#261759] shadow-sm">
          <h3 className="text-lg font-semibold">
            {t("dashboard.charts.latest")}
          </h3>
          <div className="mt-4 space-y-3">
            {(filteredEntries || []).slice(0, 5).map((entry) => (
              <div
                key={entry.id}
                className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
              >
                <div>
                  <div className="font-semibold">
                    {t("JournalEntry.numofentry")}: {entry.trans_no}
                  </div>
                  <div className="text-xs text-slate-500">
                    {entry.trans_desc || t("common.noData")}
                  </div>
                </div>
                <div className="text-xs text-slate-500">
                  {entry.trans_date_m || "-"}
                </div>
              </div>
            ))}
            {filteredEntries.length === 0 && (
              <div className="text-sm text-slate-500">{t("common.noData")}</div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Dashboard;
